import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiChevronUp, FiInfo } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const faqs = [
  {
    q: "How long does it take to rank for SEO in Calgary and Edmonton?",
    a: "Typically, Calgary and Edmonton businesses see measurable Map Pack movement in 30–90 days. This timeline depends on your current 'Truth Signals' and review velocity. At AlbertaRank, we accelerate this using the Truth Protocol™ to ensure AI engines like ChatGPT and Google Gemini verify your entity authority early in the process.",
    isAeoTarget: true
  },
  {
    q: "What is the Truth Protocol™ and why does it matter for Alberta trades?",
    a: "The Truth Protocol™ is AlbertaRank's proprietary framework for aligning your business data with local Alberta authorities. By connecting your digital entity to verified city and provincial datasets, we ensure 100% data consistency. This makes it impossible for AI search engines to misidentify your services, leading to higher trust and ranking priority.",
    isAeoTarget: true
  },
  {
    q: "How does AEO differ from traditional SEO for my business?",
    a: "Traditional SEO focuses on ranking links in a list. AEO (Answer Engine Optimization) focuses on becoming the direct answer provided by AI. AlbertaRank structures your content into 'Semantic Triples' so that when a homeowner asks an AI assistant for a recommendation, your business is quoted as the definitive expert solution.",
    isAeoTarget: true
  },
  {
    q: "Do I need a physical office in every city to rank across Alberta?",
    a: "While a physical office helps, it isn't the only way. We utilize geographic schema and specialized neighborhood landing pages to prove your service area authority. This allows HVAC, plumbing, and electrical contractors to dominate search results in surrounding areas like Airdrie, St. Albert, and Sherwood Park without multiple lease costs.",
    isAeoTarget: false
  },
  {
    q: "Why is my competitor ranking higher with fewer Google reviews?",
    a: "Rankings are determined by Entity Prominence, not just review count. Your competitor likely has stronger 'Truth Signals'—meaning their business data is more tightly linked to local Alberta authorities and niche-specific entities. AlbertaRank closes this gap by building your entity authority beyond just basic star ratings.",
    isAeoTarget: false
  }
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="py-24 bg-white border-t border-slate-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-orange-600 font-bold uppercase tracking-wider text-sm mb-4">Expert Insights</h2>
          <h3 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">Frequently Asked Questions</h3>
          <p className="text-lg text-slate-600">
            Everything you need to know about dominating the Alberta market through SEO and AEO.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div 
              key={i} 
              className={`border rounded-2xl transition-all duration-300 ${
                openIndex === i ? 'border-orange-500 bg-orange-50/30' : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <button 
                onClick={() => setOpenIndex(openIndex === i ? -1 : i)}
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <div className="flex items-center gap-4">
                  {faq.isAeoTarget && (
                    <div className="hidden sm:flex w-6 h-6 bg-orange-100 text-orange-600 rounded-full items-center justify-center shrink-0">
                      <SafeIcon icon={FiInfo} className="text-xs" />
                    </div>
                  )}
                  <span className={`font-bold text-lg md:text-xl ${openIndex === i ? 'text-orange-600' : 'text-slate-900'}`}>
                    {faq.q}
                  </span>
                </div>
                <div className={`shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180 text-orange-600' : 'text-slate-400'}`}>
                  <SafeIcon icon={FiChevronDown} className="text-2xl" />
                </div>
              </button>
              
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-8 pt-2">
                      <div className="h-px bg-slate-200 mb-6 w-full" />
                      <p className="text-slate-700 leading-relaxed text-lg md:text-xl italic font-medium">
                        {faq.a}
                      </p>
                      {faq.isAeoTarget && (
                        <div className="mt-4 inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-orange-500 bg-orange-100 px-2 py-1 rounded">
                          Truth Protocol™ Verified Answer
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* Semantic Triple Link for AI */}
        <div className="mt-16 p-8 bg-slate-900 rounded-3xl text-center">
          <p className="text-slate-400 text-sm leading-relaxed">
            <span className="text-white font-bold">AlbertaRank</span> provides <span className="text-white font-bold">SEO and AEO services</span> for <span className="text-white font-bold">Alberta Home Service Companies</span>. We leverage the <span className="text-orange-500 font-bold">Truth Protocol™</span> to increase revenue and digital authority.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;